
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNodesState, useEdgesState, addEdge, Connection, MarkerType, Node, Edge } from 'reactflow';
import { Schedule, EntitlementObject, Direction } from '../types';
import { getLayoutedElements } from '../utils/layout';
import { computeInheritance } from '../utils/inheritance';
import { isCyclic } from '../utils/validation';

interface HistoryState {
  past: string[];
  future: string[];
}

export const useGraphController = (initialData: EntitlementObject) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Undo/Redo History
  const [history, setHistory] = useState<HistoryState>({ past: [], future: [] });

  const takeSnapshot = useCallback(() => {
    setHistory(curr => {
        // Limit history size to prevent memory issues
        const newPast = [...curr.past, JSON.stringify({ nodes, edges })].slice(-50);
        return {
            past: newPast,
            future: []
        };
    });
  }, [nodes, edges]);

  const undo = useCallback(() => {
    if (history.past.length === 0) return;
    
    const previousStateStr = history.past[history.past.length - 1];
    const newPast = history.past.slice(0, -1);
    
    // Save current state to future
    const currentStateStr = JSON.stringify({ nodes, edges });
    
    const { nodes: prevNodes, edges: prevEdges } = JSON.parse(previousStateStr);
    
    // Apply previous state
    setNodes(prevNodes);
    setEdges(prevEdges);
    
    setHistory({
        past: newPast,
        future: [currentStateStr, ...history.future]
    });
  }, [history, nodes, edges, setNodes, setEdges]);

  const redo = useCallback(() => {
    if (history.future.length === 0) return;

    const nextStateStr = history.future[0];
    const newFuture = history.future.slice(1);

    // Save current to past
    const currentStateStr = JSON.stringify({ nodes, edges });

    const { nodes: nextNodes, edges: nextEdges } = JSON.parse(nextStateStr);

    setNodes(nextNodes);
    setEdges(nextEdges);

    setHistory({
        past: [...history.past, currentStateStr],
        future: newFuture
    });
  }, [history, nodes, edges, setNodes, setEdges]);

  // Load data logic extracted for reusability (Initial load + Import)
  const loadData = useCallback((data: EntitlementObject) => {
    const rawNodes: Node[] = (data.schedules || []).map((schedule: any) => {
        let direction = Direction.BottomUp;
        if (schedule.direction === Direction.TopDown || schedule.direction === 1 || schedule.direction === 'TopDown') {
            direction = Direction.TopDown;
        }

        const safeSchedule: Schedule = {
            ...schedule,
            direction: direction,
            // Ensure defaults
            isActive: schedule.isActive ?? true,
            priority: typeof schedule.priority === 'number' ? schedule.priority : 1,
            locationGroup: schedule.locationGroup || { groupId: '', nodeId: '' },
            approvalGroups: Array.isArray(schedule.approvalGroups) ? schedule.approvalGroups : [],
            resources: Array.isArray(schedule.resources) ? schedule.resources : [],
            children: Array.isArray(schedule.children) ? schedule.children : [],
            parents: Array.isArray(schedule.parents) ? schedule.parents : [],
        };

        return {
          id: safeSchedule.scheduleId,
          position: { x: 0, y: 0 },
          data: safeSchedule,
          type: 'scheduleNode',
        };
    });

    const rawEdges: Edge[] = [];
    const scheduleIds = new Set(rawNodes.map(n => n.id));

    (data.schedules || []).forEach((parent: any) => {
        if (!parent.scheduleId) return;
        const children = parent.children || [];
        children.forEach((childId: string) => {
            if (scheduleIds.has(childId) && scheduleIds.has(parent.scheduleId)) {
                rawEdges.push({
                    id: `${parent.scheduleId}-${childId}`,
                    source: parent.scheduleId,
                    target: childId,
                    type: 'smoothstep',
                    animated: false,
                    markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#94a3b8' },
                    style: { stroke: '#94a3b8', strokeWidth: 2 },
                });
            }
        });
    });

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(rawNodes, rawEdges, 'TB');
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    
    // Clear history on load
    setHistory({ past: [], future: [] });
  }, [setNodes, setEdges]);

  // Initialize Graph
  useEffect(() => {
    loadData(initialData);
  }, [initialData, loadData]);

  // Create a stable hash for checking if names changed
  const namesHash = useMemo(() => nodes.map(n => n.data.name).join('|'), [nodes]);

  // Recalculate Inheritance when topology or names change
  useEffect(() => {
    if (nodes.length === 0) return;

    setNodes((currentNodes) => {
      // 1. Reset Topology
      const nodeDataMap = new Map<string, Schedule>(
        currentNodes.map(n => [n.id, { ...n.data, parents: [], children: [] }])
      );
      
      edges.forEach(edge => {
          const sourceData = nodeDataMap.get(edge.source);
          const targetData = nodeDataMap.get(edge.target);
          if (sourceData && targetData) {
              sourceData.children.push(edge.target);
              targetData.parents.push(edge.source);
          }
      });

      // 2. Compute Inheritance
      const allSchedules = Array.from(nodeDataMap.values());
      const inheritanceMap = computeInheritance(allSchedules);

      // 3. Update Nodes
      return currentNodes.map(node => {
          const updatedData = nodeDataMap.get(node.id);
          // Only update if data actually changed to avoid unnecessary re-renders
          if (updatedData) {
              // Create a fresh object to force update
              updatedData._inheritedResources = inheritanceMap.get(node.id) || [];
              return { ...node, data: updatedData };
          }
          return node;
      });
    });
  }, [edges.length, edges, namesHash, setNodes]);

  const onConnect = useCallback((params: Connection) => {
    if (isCyclic(edges, params)) {
        alert("Cannot connect: This connection would create a cycle (loop) in the graph.");
        return;
    }
    takeSnapshot();
    setEdges((eds) => addEdge({ 
        ...params, 
        type: 'smoothstep', 
        animated: false,
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#94a3b8' },
        style: { stroke: '#94a3b8', strokeWidth: 2 },
    }, eds));
  }, [edges, setEdges, takeSnapshot]);

  const handleUpdateNode = useCallback((id: string, field: string, value: any) => {
    takeSnapshot();
    setNodes((currentNodes) => {
      // 1. Apply update to target node
      const updatedNodes = currentNodes.map((node) => {
        if (node.id === id) {
          const newData = { ...node.data, [field]: value };
          return { ...node, data: newData };
        }
        return node;
      });

      // 2. Recalculate inheritance for all nodes because a change in one (e.g. Resources, Direction, Priority)
      // can affect others or itself.
      const allSchedules = updatedNodes.map(n => n.data as Schedule);
      const inheritanceMap = computeInheritance(allSchedules);

      return updatedNodes.map(node => {
         const newInherited = inheritanceMap.get(node.id) || [];
         return {
             ...node,
             data: {
                 ...node.data,
                 _inheritedResources: newInherited
             }
         };
      });
    });
  }, [setNodes, takeSnapshot]);

  const handleLayout = useCallback(() => {
     takeSnapshot();
     const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges, 'TB');
     setNodes([...layoutedNodes]);
     setEdges([...layoutedEdges]);
  }, [nodes, edges, setNodes, setEdges, takeSnapshot]);

  const handleAddNode = useCallback((viewport: {x: number, y: number, zoom: number}) => {
    takeSnapshot();
    const id = crypto.randomUUID();
    const center = {
        x: (-viewport.x + window.innerWidth / 2) / viewport.zoom,
        y: (-viewport.y + window.innerHeight / 2) / viewport.zoom
    };

    const newNode: Node = {
        id,
        position: { x: center.x + Math.random() * 50, y: center.y + Math.random() * 50 },
        type: 'scheduleNode',
        data: {
            scheduleId: id,
            isActive: true,
            name: "New Schedule",
            time: "24/7",
            group: "Draft",
            purpose: "General",
            priority: 1,
            locationGroup: { groupId: "", nodeId: "" },
            approvalGroups: [],
            direction: Direction.BottomUp,
            tags: [],
            attributes: [],
            configuration: {},
            resources: [],
            children: [],
            parents: [],
            requirements: [],
            followUp: null,
            phaseOut: null
        } as Schedule
    };

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, takeSnapshot]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    handleUpdateNode,
    handleLayout,
    handleAddNode,
    setNodes,
    setEdges,
    undo,
    redo,
    takeSnapshot,
    loadData, // Exposed for external updates (e.g. Import)
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0
  };
};
